import React, { useState } from "react";
import { ChevronDown, Download, FileDown } from "lucide-react";
import { useApi, useDebounced } from "../hooks/useApi";
import { downloadPdf } from "./FileUpload";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { ServerPager } from "./Pager";
import { MemberMeetingsDialog, NudgeButton } from "./AttendanceExtras";
import { AttendanceFilters, AttendanceTable } from "./AttendanceTable";
import { StreakLeaderboard } from "./StreakLeaderboard";

export const qs = (o) => Object.entries(o).filter(([, v]) => v !== "" && v !== undefined && v !== null).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
const EMPTY = { q: "", role: "", status: "", date_from: "", date_to: "" };
const ATT_SIZES = [20, 50, 100];

function useCollapsed(key) {
  const [open, setOpen] = useState(() => localStorage.getItem(key) !== "0");
  const toggle = () => setOpen((o) => { localStorage.setItem(key, o ? "0" : "1"); return !o; });
  return [open, toggle];
}

export function AttendanceReport() {
  const [open, toggle] = useCollapsed("cl-attendance");
  const [f, setF] = useState({ ...EMPTY, sort: "name", order: "asc", page: 1, page_size: Number(localStorage.getItem("att-size")) || 20 });
  const set = (patch) => setF((s) => ({ ...s, ...patch, page: patch.page ?? 1 }));
  const dq = useDebounced(f.q);
  const { data } = useApi(open ? `/admin/reports/attendance?${qs({ ...f, q: dq })}` : null);
  const [detail, setDetail] = useState(null);
  const { user } = useAuth();
  const canNudge = (user?.permissions || []).includes("broadcast");
  const exportQs = qs({ ...f, page: undefined, page_size: undefined });
  const filters = { q: f.q, role: f.role, status: f.status, date_from: f.date_from, date_to: f.date_to };
  return (
    <section className="mt-8" data-testid="attendance-report">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={toggle} className="flex items-center gap-2 text-left" aria-expanded={open} data-testid="attendance-toggle">
          <ChevronDown size={18} className={`text-gold transition-transform ${open ? "" : "-rotate-90"}`} />
          <h3 className="font-serif-display text-xl font-bold text-crimson">Attendance report {data?.year}
            {data && <span className="ml-2 font-mono-accent text-sm text-muted-foreground">({data.met}/{data.total} met · {data.meetings} meetings)</span>}</h3>
        </button>
        {open && (
          <div className="flex gap-2">
            {canNudge && <NudgeButton filters={filters} below={data ? data.nudgeable : null} />}
            <Button size="sm" variant="outline" className="gap-1" data-testid="attendance-export-csv" onClick={() => downloadPdf(`/admin/reports/attendance.csv?${exportQs}`, "attendance-report.csv")}><Download size={14} /> CSV</Button>
            <Button size="sm" className="gap-1 bg-crimson hover:bg-crimson-hover" data-testid="attendance-export-pdf" onClick={() => downloadPdf(`/admin/reports/attendance.pdf?${exportQs}`, "attendance-report.pdf")}><FileDown size={14} /> PDF</Button>
          </div>
        )}
      </div>
      {open && (
        <div className="mt-3" data-testid="attendance-body">
          <AttendanceFilters f={f} set={set} onClear={() => set(EMPTY)} />
          <div className="glass overflow-x-auto rounded-2xl">
            <AttendanceTable rows={data?.members} sort={f.sort} order={f.order} onSort={(k) => set({ sort: k, order: f.sort === k && f.order === "asc" ? "desc" : "asc" })} onRow={setDetail} />
            {data && <ServerPager page={data.page} pages={data.pages} total={data.total} pageSize={data.page_size} sizes={ATT_SIZES} noun="members"
              onPage={(p) => set({ page: p })} onSize={(n) => { localStorage.setItem("att-size", String(n)); set({ page_size: n }); }} testId="attendance-pager" />}
          </div>
          <MemberMeetingsDialog member={detail} range={f} onClose={() => setDetail(null)} />
          <StreakLeaderboard />
        </div>
      )}
    </section>
  );
}
